import { rotuloTipoCampo, type TipoCampo } from '@qsms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CampoBusca } from '../componentes/CampoBusca.js'
import { api, type CampoCatalogoAdmin, type ChecklistDetalheAdmin, type CorpoChecklist } from '../dados/api.js'
import { sincronizar } from '../dados/sincronizacao.js'
import { PainelAdmin, SemRedeAqui } from './admin-comum.js'
import { semAcento } from './Checklists.js'

/// Monta um checklist escolhendo perguntas do catálogo. Não cria pergunta: o
/// catálogo é a base, e o tipo e as opções de cada campo vêm dele — como no Power
/// Apps, onde as propriedades do campo "são globais".
///
/// Três entradas, a mesma tela:
/// - `/checklists/novo` — em branco;
/// - `/checklists/novo?de=<id>` — duplicar: os campos de outro checklist, para
///   virar um checklist do sistema (é assim que se "edita" um do Power Apps);
/// - `/checklists/<id>` — editar um do sistema, ou só ver um do Power Apps.

interface Item {
  /// Chave da lista na tela; o `id` só existe para campo que já está salvo.
  chave: string
  id?: string
  campoCatalogoId: string
  secao: string
  itemCode: string
  obrigatorio: boolean
}

interface Cabecalho {
  nome: string
  codigo: string
  categoria: string
  revisao: string
  descricao: string
}

/// As seções que os checklists do Power Apps mais usam, para sugerir.
const SECOES_COMUNS = ['CABEÇALHO', 'ITENS IMPEDITIVOS', 'ITENS A VERIFICAR', 'LISTA DE VERIFICAÇÃO', 'CONSIDERAÇÕES GERAIS', 'AVALIAÇÃO']
const CATEGORIAS = ['RA', 'RGE']
/// Resultados desenhados de uma vez. Mais do que isso, a busca precisa afinar.
const LIMITE_RESULTADOS = 80

let sequencia = 0
const novaChave = () => `n${++sequencia}`

export function EditorChecklist() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const de = params.get('de')
  const modo: 'novo' | 'duplicar' | 'editar' = id ? 'editar' : de ? 'duplicar' : 'novo'
  const origemId = id ?? de

  const catalogo = useQuery({
    queryKey: ['admin', 'catalogo'],
    queryFn: () => api.adminCatalogo(),
    staleTime: 10 * 60_000,
  })
  // Sempre do servidor ao abrir: o formulário nasce destes campos, e montá-lo de
  // um cache antigo mandaria de volta, ao salvar, um campo que outra pessoa já tirou.
  const base = useQuery({
    queryKey: ['admin', 'checklist', origemId],
    queryFn: () => api.adminChecklist(origemId!),
    enabled: Boolean(origemId),
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  // Erro de uma busca em segundo plano, com dados na mão, não apaga a edição em
  // andamento: só vale quando não há o que desenhar.
  const erro = (!catalogo.data ? catalogo.error : null) ?? (origemId && !base.data ? base.error : null)
  if (erro) {
    return (
      <PainelAdmin titulo="Checklist" subtitulo="Montado com os campos do catálogo">
        <SemRedeAqui erro={erro} />
      </PainelAdmin>
    )
  }
  if (!catalogo.data || (origemId && (!base.data || !base.isFetchedAfterMount))) {
    return <p className="py-10 text-center text-slate-500">Carregando o catálogo…</p>
  }
  if (modo === 'editar' && !base.data!.editavel) {
    return <SoLeitura checklist={base.data!} catalogo={catalogo.data} />
  }
  // A `key` refaz o formulário do zero ao trocar de checklist pela barra de endereço.
  return <Formulario key={`${modo}:${origemId ?? ''}`} modo={modo} base={base.data} catalogo={catalogo.data} />
}

function Formulario({
  modo,
  base,
  catalogo,
}: {
  modo: 'novo' | 'duplicar' | 'editar'
  base: ChecklistDetalheAdmin | undefined
  catalogo: CampoCatalogoAdmin[]
}) {
  const navegar = useNavigate()
  const cliente = useQueryClient()
  const porId = useMemo(() => new Map(catalogo.map((c) => [c.id, c])), [catalogo])

  const [cab, setCab] = useState<Cabecalho>(() => ({
    nome: base ? (modo === 'duplicar' ? `${base.nome} (cópia)` : base.nome) : '',
    codigo: base?.codigo ?? '',
    categoria: base?.categoria ?? '',
    revisao: base?.revisao ?? '',
    descricao: base?.descricao ?? '',
  }))
  // Campo sem pergunta do catálogo (há alguns no Power Apps) não tem como ir para
  // um checklist novo: fica de fora, e a tela avisa quantos.
  const [itens, setItens] = useState<Item[]>(() =>
    (base?.campos ?? [])
      .filter((c) => c.campoCatalogoId && porId.has(c.campoCatalogoId))
      .map((c) => ({
        chave: novaChave(),
        id: modo === 'editar' ? c.id : undefined,
        campoCatalogoId: c.campoCatalogoId!,
        secao: c.secao ?? '',
        itemCode: c.itemCode ?? '',
        obrigatorio: c.obrigatorio,
      })),
  )
  const deixadosDeFora = (base?.campos ?? []).filter((c) => !c.campoCatalogoId || !porId.has(c.campoCatalogoId)).length
  const [motivo, setMotivo] = useState('')

  const salvar = useMutation({
    mutationFn: () => {
      const corpo: CorpoChecklist = {
        nome: cab.nome.trim(),
        codigo: cab.codigo.trim() || null,
        categoria: cab.categoria.trim() || null,
        revisao: cab.revisao.trim() || null,
        descricao: cab.descricao.trim() || null,
        campos: itens.map((i) => ({
          id: i.id,
          campoCatalogoId: i.campoCatalogoId,
          secao: i.secao.trim() || null,
          itemCode: i.itemCode.trim() || null,
          obrigatorio: i.obrigatorio,
        })),
        motivo: motivo.trim() || undefined,
        atualizadoEm: modo === 'editar' ? base?.atualizadoEm : undefined,
      }
      return modo === 'editar' && base ? api.adminSalvarChecklist(base.id, corpo) : api.adminCriarChecklist(corpo)
    },
    onSuccess: (r) => {
      void cliente.invalidateQueries({ queryKey: ['admin'] })
      // Traz o checklist para este aparelho já: quem acabou de criar quer vê-lo em
      // "Nova ação" sem esperar a próxima volta da sincronização.
      void sincronizar()
      navegar('/checklists', {
        state: { aviso: `"${r.nome}" foi salvo. Ele aparece em "Nova ação" assim que cada aparelho sincronizar.` },
      })
    },
  })

  function mover(i: number, delta: number) {
    setItens((atual) => {
      const j = i + delta
      if (j < 0 || j >= atual.length) return atual
      const novo = [...atual]
      ;[novo[i], novo[j]] = [novo[j]!, novo[i]!]
      return novo
    })
  }
  function mudar(i: number, parte: Partial<Item>) {
    setItens((atual) => atual.map((it, k) => (k === i ? { ...it, ...parte } : it)))
  }
  function remover(i: number) {
    setItens((atual) => atual.filter((_, k) => k !== i))
  }
  function adicionar(ids: string[], secao: string) {
    setItens((atual) => [
      ...atual,
      ...ids.map((campoCatalogoId) => ({ chave: novaChave(), campoCatalogoId, secao, itemCode: '', obrigatorio: false })),
    ])
  }

  const secoesUsadas = [...new Set(itens.map((i) => i.secao.trim()).filter(Boolean))]
  const sugestoes = [...new Set([...secoesUsadas, ...SECOES_COMUNS])]
  const separadas = secoesSeparadas(itens)
  // Campo que passa a ser obrigatório num checklist em uso: quem finalizou sem
  // internet, na versão anterior, tem a inspeção devolvida para completar.
  const obrigatoriosAntes = new Set((base?.campos ?? []).filter((c) => c.obrigatorio).map((c) => c.id))
  const novosObrigatorios =
    modo === 'editar' && (base?.inspecoes ?? 0) > 0
      ? itens.filter((i) => i.obrigatorio && !(i.id && obrigatoriosAntes.has(i.id))).length
      : 0
  const problema =
    cab.nome.trim().length < 3
      ? 'Dê um nome ao checklist.'
      : itens.length === 0
        ? 'Escolha ao menos um campo do catálogo.'
        : null

  return (
    <PainelAdmin
      titulo={modo === 'editar' ? 'Editar checklist' : modo === 'duplicar' ? 'Duplicar checklist' : 'Novo checklist'}
      subtitulo="As perguntas vêm do catálogo de campos: escolha, ordene e agrupe em seções"
    >
      <Link to="/checklists" className="mb-3 inline-block text-sm font-medium text-marinho">
        ← Checklists
      </Link>

      <datalist id="secoes-checklist">
        {sugestoes.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>

      <div className="cartao grid gap-3 p-4 lg:grid-cols-4">
        <label className="block lg:col-span-4">
          <span className="text-xs font-semibold text-slate-600">Nome *</span>
          <input
            value={cab.nome}
            onChange={(e) => setCab({ ...cab, nome: e.target.value })}
            placeholder="Ex.: RA-070-001 - Check List Diário - Guindaste (Rev.00)"
            maxLength={200}
            className="campo mt-1"
          />
          <span className="mt-1 block text-xs text-slate-500">
            É o que o inspetor vê ao escolher o checklist. Os do Power Apps levam código e revisão no nome.
          </span>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Código</span>
          <input value={cab.codigo} onChange={(e) => setCab({ ...cab, codigo: e.target.value })} placeholder="RA-070-001" maxLength={40} className="campo mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Categoria</span>
          <input value={cab.categoria} onChange={(e) => setCab({ ...cab, categoria: e.target.value })} list="categorias-checklist" maxLength={40} className="campo mt-1" />
          <datalist id="categorias-checklist">
            {CATEGORIAS.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Revisão</span>
          <input value={cab.revisao} onChange={(e) => setCab({ ...cab, revisao: e.target.value })} placeholder="Rev.00" maxLength={20} className="campo mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Descrição</span>
          <input value={cab.descricao} onChange={(e) => setCab({ ...cab, descricao: e.target.value })} maxLength={1000} className="campo mt-1" />
        </label>
      </div>

      {modo === 'duplicar' && base?.origem === 'POWER_APPS' && (
        <p className="mt-3 rounded bg-marinho-50 px-3 py-2 text-sm text-marinho">
          Na cópia, cada campo segue o catálogo. Alguns podem aparecer diferentes do original — uma pergunta que no
          Power Apps virou texto livre, por exemplo, volta a ter as opções Sim / Não / NA.
        </p>
      )}

      {deixadosDeFora > 0 && (
        <p className="mt-3 rounded bg-moderada/10 px-3 py-2 text-sm text-moderada">
          {deixadosDeFora} {deixadosDeFora === 1 ? 'campo ficou' : 'campos ficaram'} de fora: não {deixadosDeFora === 1 ? 'tem' : 'têm'} pergunta
          no catálogo.
        </p>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2 lg:items-start">
        <section className="cartao p-4">
          <h3 className="mb-1 font-semibold text-slate-800">Campos do checklist ({itens.length})</h3>
          <p className="mb-3 text-xs text-slate-500">
            Na ordem em que aparecem no formulário. Campos da mesma seção aparecem juntos, sob o título dela.
            {modo === 'editar' && ' Tirar um campo não apaga as respostas já dadas nele.'}
          </p>
          {itens.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              Nenhum campo ainda. Escolha no catálogo.
            </p>
          ) : (
            <ol className="space-y-1.5">
              {itens.map((it, i) => {
                const cat = porId.get(it.campoCatalogoId)!
                const titulo = it.secao.trim() && it.secao.trim() !== (itens[i - 1]?.secao.trim() ?? '')
                return (
                  <li key={it.chave}>
                    {titulo && (
                      <p className="mb-1 mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{it.secao.trim()}</p>
                    )}
                    <div className="flex items-start gap-2 rounded-lg border border-slate-200 p-2">
                      <span className="w-6 shrink-0 pt-0.5 text-right font-mono text-xs text-slate-400">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800">{cat.texto}</p>
                        <DescricaoTipo tipo={cat.tipoCampo} opcoes={cat.opcoes} />
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <input
                            value={it.secao}
                            onChange={(e) => mudar(i, { secao: e.target.value })}
                            list="secoes-checklist"
                            placeholder="Seção"
                            maxLength={120}
                            aria-label={`Seção de ${cat.texto}`}
                            className="campo !w-52 !py-1"
                          />
                          <input
                            value={it.itemCode}
                            onChange={(e) => mudar(i, { itemCode: e.target.value })}
                            placeholder="Item"
                            maxLength={20}
                            aria-label={`Número do item de ${cat.texto}`}
                            className="campo !w-20 !py-1"
                          />
                          <label className="flex items-center gap-1.5 text-xs text-slate-700">
                            <input
                              type="checkbox"
                              checked={it.obrigatorio}
                              onChange={(e) => mudar(i, { obrigatorio: e.target.checked })}
                              aria-label={`Obrigatório: ${cat.texto}`}
                              className="h-4 w-4 rounded border-slate-300"
                            />
                            Obrigatório
                          </label>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1">
                        <BotaoIcone rotulo={`Subir ${cat.texto}`} onClick={() => mover(i, -1)} desativado={i === 0}>
                          ↑
                        </BotaoIcone>
                        <BotaoIcone rotulo={`Descer ${cat.texto}`} onClick={() => mover(i, 1)} desativado={i === itens.length - 1}>
                          ↓
                        </BotaoIcone>
                        <BotaoIcone rotulo={`Tirar ${cat.texto}`} onClick={() => remover(i)} perigo>
                          ✕
                        </BotaoIcone>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ol>
          )}
          {separadas.length > 0 && (
            <p className="mt-3 rounded bg-moderada/10 px-3 py-2 text-xs text-moderada">
              {separadas.length === 1 ? 'A seção' : 'As seções'} {separadas.map((s) => `"${s}"`).join(', ')}{' '}
              {separadas.length === 1 ? 'aparece' : 'aparecem'} em partes separadas da lista. No formulário, os campos de
              cada seção ficam juntos, na posição da primeira vez.
            </p>
          )}
        </section>

        <SeletorCatalogo catalogo={catalogo} jaNoChecklist={new Set(itens.map((i) => i.campoCatalogoId))} onAdicionar={adicionar} />
      </div>

      <div className="cartao mt-4 space-y-3 p-4 lg:flex lg:items-end lg:gap-3 lg:space-y-0">
        <label className="block lg:flex-1">
          <span className="text-xs font-semibold text-slate-600">Motivo (fica no registro)</span>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: novo checklist do guindaste da obra 0904" maxLength={500} className="campo mt-1" />
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => salvar.mutate()}
            disabled={Boolean(problema) || salvar.isPending}
            className="botao-primario flex-1 lg:w-48 lg:flex-none"
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar checklist'}
          </button>
          <Link to="/checklists" className="botao-neutro flex-1 lg:w-32 lg:flex-none">
            Cancelar
          </Link>
        </div>
      </div>
      {novosObrigatorios > 0 && (
        <p className="mt-2 rounded bg-moderada/10 px-3 py-2 text-sm text-moderada">
          {novosObrigatorios === 1 ? 'Um campo passa' : `${novosObrigatorios} campos passam`} a ser obrigatório
          {novosObrigatorios === 1 ? '' : 's'}. Este checklist já tem inspeções: quem finalizou uma sem internet, na
          versão anterior, vai recebê-la de volta para completar.
        </p>
      )}
      {(problema || salvar.isError) && (
        <p className="mt-2 text-sm text-alta" role="alert">
          {problema ?? (salvar.error as Error).message}
        </p>
      )}
    </PainelAdmin>
  )
}

/// A busca no catálogo: sem acento, por texto ou código, e por tipo. Marca-se
/// quantas perguntas quiser e elas entram juntas, na ordem em que foram marcadas.
function SeletorCatalogo({
  catalogo,
  jaNoChecklist,
  onAdicionar,
}: {
  catalogo: CampoCatalogoAdmin[]
  jaNoChecklist: Set<string>
  onAdicionar: (ids: string[], secao: string) => void
}) {
  const [busca, setBusca] = useState('')
  const [tipo, setTipo] = useState<TipoCampo | ''>('')
  const [marcados, setMarcados] = useState<string[]>([])
  const [secao, setSecao] = useState('')

  const indice = useMemo(() => catalogo.map((c) => ({ c, chave: semAcento(`${c.texto} ${c.codigo}`) })), [catalogo])
  const tipos = useMemo(() => [...new Set(catalogo.map((c) => c.tipoCampo))], [catalogo])
  const achados = useMemo(() => {
    const termo = semAcento(busca.trim())
    return indice.filter(({ c, chave }) => (!tipo || c.tipoCampo === tipo) && (!termo || chave.includes(termo))).map((x) => x.c)
  }, [indice, busca, tipo])

  // Busca nova começa sem a lista embaixo do dedo mudar de lugar a cada letra.
  useEffect(() => {
    const el = document.getElementById('resultados-catalogo')
    if (el) el.scrollTop = 0
  }, [busca, tipo])

  function alternar(id: string) {
    setMarcados((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]))
  }

  return (
    <section className="cartao p-4 lg:sticky lg:top-20">
      <h3 className="mb-1 font-semibold text-slate-800">Catálogo de campos ({catalogo.length.toLocaleString('pt-BR')})</h3>
      <p className="mb-3 text-xs text-slate-500">Só perguntas que já existem na base. O tipo e as opções vêm daqui.</p>
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <CampoBusca valor={busca} aoMudar={setBusca} placeholder="Buscar pergunta (ex.: cinto, andaime)" />
        <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoCampo | '')} className="campo" aria-label="Tipo de campo">
          <option value="">Todos os tipos</option>
          {tipos.map((t) => (
            <option key={t} value={t}>
              {rotuloTipoCampo[t]}
            </option>
          ))}
        </select>
      </div>

      <p className="mb-1 mt-2 text-xs text-slate-500">
        {achados.length === 0
          ? 'Nenhuma pergunta com essa busca.'
          : achados.length > LIMITE_RESULTADOS
            ? `${achados.length.toLocaleString('pt-BR')} perguntas; mostrando as ${LIMITE_RESULTADOS} primeiras. Refine a busca.`
            : `${achados.length} ${achados.length === 1 ? 'pergunta' : 'perguntas'}`}
      </p>
      <ul id="resultados-catalogo" className="max-h-[26rem] space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-1">
        {achados.slice(0, LIMITE_RESULTADOS).map((c) => {
          const marcado = marcados.includes(c.id)
          return (
            <li key={c.id}>
              <label className={`flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-slate-50 ${marcado ? 'bg-marinho-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={marcado}
                  onChange={() => alternar(c.id)}
                  aria-label={c.texto}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-slate-800">{c.texto}</span>
                  <DescricaoTipo tipo={c.tipoCampo} opcoes={c.opcoes} />
                  <span className="mt-0.5 flex flex-wrap gap-1">
                    {jaNoChecklist.has(c.id) && <span className="etiqueta bg-limao/20 text-marinho-900">já neste checklist</span>}
                    {c.impeditivo && <span className="etiqueta bg-alta/10 text-alta">impeditivo</span>}
                    <span className="text-[11px] text-slate-400">
                      em {c.usos} {c.usos === 1 ? 'checklist' : 'checklists'}
                    </span>
                  </span>
                </span>
              </label>
            </li>
          )
        })}
      </ul>

      <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Seção dos campos marcados</span>
          <input value={secao} onChange={(e) => setSecao(e.target.value)} list="secoes-checklist" placeholder="Opcional" maxLength={120} className="campo mt-1" />
        </label>
        <button
          onClick={() => {
            onAdicionar(marcados, secao.trim())
            setMarcados([])
          }}
          disabled={marcados.length === 0}
          className="botao-destaque"
        >
          Adicionar {marcados.length > 0 ? marcados.length : ''} ao checklist
        </button>
      </div>
    </section>
  )
}

/// Checklist que não se edita aqui: o INI, ou um que veio do Power Apps.
function SoLeitura({ checklist, catalogo }: { checklist: ChecklistDetalheAdmin; catalogo: CampoCatalogoAdmin[] }) {
  const porId = new Map(catalogo.map((c) => [c.id, c]))
  return (
    <PainelAdmin titulo="Campos do checklist" subtitulo={checklist.nome}>
      <Link to="/checklists" className="mb-3 inline-block text-sm font-medium text-marinho">
        ← Checklists
      </Link>
      <div className="mb-3 rounded bg-marinho-50 px-3 py-2 text-sm text-marinho">
        {checklist.tipo === 'INI'
          ? 'O checklist INI tem regras próprias no formulário e não é editado por esta tela.'
          : 'Este checklist veio do Power Apps e não se edita aqui: a carga final do Dataverse o reescreveria. Duplique para ter uma cópia do sistema, que se edita à vontade.'}
      </div>
      {checklist.tipo !== 'INI' && (
        <Link to={`/checklists/novo?de=${checklist.id}`} className="botao-primario mb-4 !min-h-0 !py-2 text-sm">
          Duplicar este checklist
        </Link>
      )}
      <ol className="grid gap-1.5 lg:grid-cols-2">
        {checklist.campos.map((c, i) => {
          const cat = c.campoCatalogoId ? porId.get(c.campoCatalogoId) : undefined
          return (
            <li key={c.id} className="cartao flex items-start gap-2 p-2">
              <span className="w-6 shrink-0 pt-0.5 text-right font-mono text-xs text-slate-400">{i + 1}</span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{c.nome}</p>
                <DescricaoTipo tipo={comoOInspetorVe(c.tipoCampo, c.opcoes)} opcoes={c.opcoes} />
                {cat && comoOInspetorVe(c.tipoCampo, c.opcoes) !== cat.tipoCampo && (
                  <span className="block text-[11px] text-moderada">
                    Numa cópia: {rotuloTipoCampo[cat.tipoCampo]}
                    {cat.opcoes.length > 0 && ` · ${cat.opcoes.slice(0, 4).join(' / ')}`}
                  </span>
                )}
                {c.secao && <p className="text-[11px] uppercase tracking-wide text-slate-400">{c.secao}</p>}
              </div>
            </li>
          )
        })}
      </ol>
    </PainelAdmin>
  )
}

/// O tipo com que o formulário desenha o campo: texto com opções cadastradas vira
/// escolha (ver CampoResposta).
function comoOInspetorVe(tipo: TipoCampo, opcoes: string[]): TipoCampo {
  return tipo === 'TEXTO' && opcoes.length > 0 ? 'SELECAO_UNICA' : tipo
}

function DescricaoTipo({ tipo, opcoes }: { tipo: TipoCampo; opcoes: string[] }) {
  const mostrar = opcoes.slice(0, 4).join(' / ')
  return (
    <span className="block text-xs text-slate-500">
      {rotuloTipoCampo[tipo]}
      {opcoes.length > 0 && ` · ${mostrar}${opcoes.length > 4 ? ` e mais ${opcoes.length - 4}` : ''}`}
    </span>
  )
}

function BotaoIcone({
  rotulo,
  onClick,
  desativado,
  perigo,
  children,
}: {
  rotulo: string
  onClick: () => void
  desativado?: boolean
  perigo?: boolean
  children: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={desativado}
      aria-label={rotulo}
      title={rotulo}
      className={`grid h-8 w-8 place-items-center rounded border text-sm disabled:opacity-30 ${
        perigo ? 'border-alta/30 text-alta hover:bg-alta/5' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  )
}

/// Seções que aparecem em mais de um trecho da lista. O formulário agrupa por
/// seção na ordem da primeira aparição, então o segundo trecho "pula" para cima.
function secoesSeparadas(itens: Item[]): string[] {
  const vistas = new Set<string>()
  const separadas = new Set<string>()
  let anterior: string | null = null
  for (const it of itens) {
    const s = it.secao.trim()
    if (s !== anterior) {
      if (vistas.has(s)) separadas.add(s || 'sem seção')
      vistas.add(s)
      anterior = s
    }
  }
  return [...separadas]
}
